/**
 * Full-viewport interaction lock while a PWA safe refresh is in flight.
 * Portaled to document.body so #root can be inert without hiding this UI.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../shared/ui/surfacePrimitives.js';

export interface PwaRefreshBlockingOverlayProps {
  readonly label: string;
  readonly testId?: string;
  readonly recoveryHint?: string;
  readonly recoveryRetryLabel?: string;
  readonly recoveryOpenLinkLabel?: string;
  readonly recoveryOpenLinkHref?: string;
  readonly onRecoveryRetry?: () => void;
}

export function PwaRefreshBlockingOverlay({
  label,
  testId = 'admin-pwa-refresh-blocking',
  recoveryHint,
  recoveryRetryLabel,
  recoveryOpenLinkLabel,
  recoveryOpenLinkHref,
  onRecoveryRetry,
}: PwaRefreshBlockingOverlayProps): JSX.Element | null {
  const showRecovery =
    recoveryHint !== undefined &&
    recoveryRetryLabel !== undefined &&
    onRecoveryRetry !== undefined;

  useEffect(() => {
    const root = document.getElementById('root');
    const previousOverflow = document.body.style.overflow;
    root?.setAttribute('inert', '');
    root?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = 'hidden';

    const blockKeys = (event: KeyboardEvent): void => {
      if (showRecovery && event.key === 'Tab') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', blockKeys, true);

    return () => {
      root?.removeAttribute('inert');
      root?.removeAttribute('aria-hidden');
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', blockKeys, true);
    };
  }, [showRecovery]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-toast)] flex items-center justify-center bg-black/45 p-6 backdrop-blur-[2px] motion-reduce:backdrop-blur-none"
      role="alertdialog"
      aria-modal="true"
      aria-busy={showRecovery ? undefined : true}
      aria-labelledby="admin-pwa-refresh-label"
      data-testid={testId}
      onContextMenu={(event) => {
        event.preventDefault();
      }}
    >
      <div className="max-w-sm rounded-md border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] px-4 py-3 shadow-[var(--elevation-2)]">
        <p
          id="admin-pwa-refresh-label"
          className="text-sm text-[var(--color-an-text)]"
        >
          {label}
        </p>
        {showRecovery ? (
          <div className="mt-3 space-y-2" data-testid="admin-pwa-refresh-recovery">
            <p className="text-xs text-[var(--color-an-text-muted)]">{recoveryHint}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                intent="primary"
                size="sm"
                onClick={onRecoveryRetry}
                data-testid="admin-pwa-refresh-retry"
              >
                {recoveryRetryLabel}
              </Button>
              {recoveryOpenLinkLabel !== undefined &&
              recoveryOpenLinkHref !== undefined ? (
                <Button
                  type="button"
                  intent="secondary"
                  size="sm"
                  onClick={() => {
                    window.location.assign(recoveryOpenLinkHref);
                  }}
                  data-testid="admin-pwa-refresh-open-link"
                >
                  {recoveryOpenLinkLabel}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
